import { firstLetterUpper } from 'projects/common/src/app/services/utils/utilities';
import { lang } from 'moment';
import { Component, OnDestroy, OnInit } from '@angular/core';
import Highcharts from 'highcharts';
import { IMachineContext } from 'projects/common/src/app/interfaces/machine-context';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { IShift } from 'src/app/modules/core/interfaces';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { IProductionHour } from 'src/app/modules/widget/interfaces/production-hour';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { MachineProductionService } from '../../../services/machine-production/machine-production.service';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';

@Component({
  selector: 'app-production-by-hour',
  templateUrl: './production-by-hour.component.html',
  styleUrls: ['./production-by-hour.component.scss'],
})
export class ProductionByHourComponent extends DashboardWidgetChartComponent implements OnInit, OnDestroy {
  public displayMessage = false;
  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    public langService: LanguageService,
    private datePipe: DatePresentationPipe,
    private machineProductionService: MachineProductionService,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  protected initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
      hasZone: true,
    };
  }

  /**
   * Function to draw the chart
   *
   * @param chartOptions ChartOptions
   */
  public drawView(chartOptions: IProductionHour[]): void {
    this.chartWithoutData$.next(false);
    const machineType = this.parameterService.parameters.item('machine-context').value as IMachineContext;
    this.hasData = !!chartOptions.length;

    if (chartOptions.length) {
      this.hasData = true;
      const hoursAxis = chartOptions.map((value) => {
        return value.hourPeriodNumber < 10 ? `0${value.hourPeriodNumber}:00` : `${value.hourPeriodNumber}:00`;
      });
      const dataSeries = this.machineProductionService.createProductionSerieByHour(machineType.name.toLowerCase(), chartOptions);

      const allZeros = dataSeries.barSerie.data.every((value) => value === 0 || value === null);

      if (allZeros) {
        this.chartWithoutData$.next(true);
      } else {
        this.chartWithoutData$.next(false);
      }

      let series = [];
      if (chartOptions[0]?.itemExpected > 0) {
        series = [dataSeries.barSerie, dataSeries.firstLineSerie, dataSeries.expectedLineSerie];
      } else {
        series = [dataSeries.barSerie, dataSeries.firstLineSerie];
      }

      const date = this.parameterService.parameters.item('date').value;
      const shift = this.parameterService.parameters.item('shift').value as IShift;
      const user = this.parameterService.parameters.item('user-data').value;
      const shiftTranslation = this.langService.message('shift_');
      const hoursTranslation = this.langService.message('hours');
      const data = {
        chart: {
          zoomType: 'xy',
          backgroundColor: '#F8F8F8',
        },
        title: {
          text: '',
        },
        subtitle: {
          text: `${this.langService.message('company')}: ${user.company.name},
                    ${this.langService.message('device')}: ${this.langService.message(machineType.name)},
                    ${this.langService.message('date')}: ${this.datePipe.transform(date)},
                    ${this.langService.message('shift_')}: ${shift.name}`,
        },
        credits: {
          enabled: false,
        },
        plotOptions: {
          series: {
            events: {
              legendItemClick() {
                return false;
              },
            },
            lineWidth: 1,
            marker: {
              symbol: 'circle',
            },
            borderColor: '#22D983',
            borderWidth: 2,
            dataLabels: {
              style: {
                fontSize: '11px',
                fontWeight: 600,
              }     
            }
          },
        },
        tooltip: {
          outside: true,
          backgroundColor: '#433e3e',
          borderColor: '#433e3e',
          borderRadius: 2,
          style: {
            color: 'white',
            opacity: 1,
          },
          useHTML: true,
          formatter(): string {
            let divSymbol = '';
            if (this.series.userOptions.itemType === 1) {
              divSymbol = `
            <div style="background-color: #22d983;
              width: 35px;
              height: 15px;
              border: 1px solid #20D581;
              float: left;
              margin-top: 2px">
            </div>`;
            }
            if (this.series.userOptions.itemType === 2) {
              divSymbol = `
            <div style="float: left;
              margin-top: -1px;">
            </div>`;
            }
            if (this.series.userOptions.itemType === 3) {
              divSymbol = `
            <div style="background-color: #FF9966;
              width: 35px;
              height: 15px;
              border: 1px solid #FF9966;
              float: left;
              margin-top: 2px">
            </div>`;
            }

            const { singularUnit, pluralUnit } = this.series.userOptions.meditionUnit;

            return `
          <div style="width: 180px">
            <strong>${shiftTranslation} ${shift.name}</strong> <br>
            <span>${this.key} ${hoursTranslation}</span> <br>
            ${divSymbol} &nbsp;
            <span>${this.y} ${this.y === 0 || this.y > 1 ? firstLetterUpper(pluralUnit) : firstLetterUpper(singularUnit)}</span>
          </div>`;
          },
        },
        legend: {
          layout: 'horizontal',
          verticalAlign: 'top',
          useHTML: true,
          squareSymbol: false,
          align: 'center',
          labelFormatter(): string {
            let figure = '';
            if (this.userOptions.itemType === 1) {
              figure = `
            <svg width="30" height="18" style="float:left; margin-right: 5px !important;">
              <rect width="28" height="12" x="2" y="2"
              style="fill: ${'#22D983'}; stroke-width: 2px !important;
              stroke: ${'#22D983'} !important;"/>
            </svg>`;
              return `<div style="width: 200px; margin-top: -2px">
            ${figure} 
                          <span style="color: black">${this.name}</span>
                        </div>`;
            }
            if (this.userOptions.itemType === 2) {
              return `<div style="width: 200px">
                          <div style="color: #707070; float: left; font-size: 11px">
                            - - -
                          </div>
                          <span style="color: black">&nbsp; ${this.name}</span>
                        </div>`;
            }
            if (this.userOptions.itemType === 3) {
              figure = `
            <svg width="30" height="18" style="float:left; margin-right: 5px !important;">
              <rect width="28" height="12" x="2" y="2" 
              style="fill: ${'#FF9966'}; stroke-width: 2px !important; 
              stroke: ${'#FF9966'} !important"/>
            </svg>`;

              return `<div style="width: 200px; margin-top: -2px">
            ${figure} 
                          <span style="color: black">${this.name}</span>
                        </div>`;
            }
          },
          symbolHeight: 0.001,
          symbolWidth: 0.001,
          symbolRadius: 0.001,
        },
        xAxis: {
          title: {
            text: this.langService.message('hours').toUpperCase(),
          },
          categories: hoursAxis,
        },
        yAxis: [
          {
            title: {
              text: `${this.langService.message(dataSeries.xAxisLeft).toUpperCase()}`,
            },
            opposite: false,
          },
          {
            title: {
              text: `${this.langService.message(dataSeries.xAxisRight).toUpperCase()}`,
            },
            opposite: true,
          },
        ],
        series,
      };
      this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
        if (!this.hasData) {
          const newX = chart.plotWidth / 3 + chart.plotLeft;
          const newY = chart.plotHeight / 2 + chart.plotTop;
          chart.renderer
            .text('La solicitud no arrojó resultados', newX, newY)
            .css({
              color: '#4572A7',
              fontSize: '24px',
            })
            .add();
        }
      });
    } else {
      this.displayMessage = true;
      this.hasData = false;
    }
  }

  /**
   * On destroy
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
